import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ContactForm from '@/components/feature/ContactForm';
import { categories, siteName } from '@/mocks/articles';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/base/JsonLd';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '交叉信源验证',
    text: '对关键爆料坚持多方交叉核对，只呈现有时间点、可追溯的信息，降低单一信源带来的误报风险。',
  },
  {
    icon: 'ri-question-line',
    title: '诚实标注不确定性',
    text: '凡属“疑似/据传”的内容都会明确标注，区分真机谍照与概念渲染，绝不把爆料说成官方定论。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '中立客观解读',
    text: '不站队、不夸大，用参数与事实说话。评测与解读皆基于可验证信息，向读者提供理性参考。',
  },
];

const steps = [
  { tag: '创立初衷', title: '填补信息差', text: '新机发布前的爆料往往零散难辨。我们希望把手机参数曝光、谍照与价格线索集中起来，让普通用户也能和行业同步看到信息。' },
  { tag: '内容体系', title: '栏目化、可追溯', text: '把内容沉淀为参数、谍照、价格、内幕与解读五类栏目，每一条都可回溯信源阶段，方便读者判断价值。' },
  { tag: '长期方向', title: '陪伴每一次换机', text: '从“看热闹”到“帮决策”，我们希望成为你每次了解新机、规划预算前都会打开的内容伙伴。' },
];

const aboutReviews = [
  { name: '老周（广州 · 工程师）', text: '关注一年多，最大的感受是克制。不是所有爆料都发，能被放出来的基本都有信源背书，这在爆料圈很稀缺。' },
  { name: '晓彤（杭州 · 新媒体）', text: '内容更新快又不水，价格预测板块很靠谱。上次新机发布前按这里的区间做功课，预算规划精准多了。' },
];

export default function About() {
  usePageMeta(
    '关于我们',
    `了解${siteName}：品牌介绍、内容定位与发展理念。我们坚持交叉信源验证，专注手机参数曝光、未发布产品图、配置价格预测与厂商内部消息。`
  );

  return (
    <>
      <Navbar />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: `关于${siteName}`,
          description: '每日新料品牌介绍、内容原则与发展理念。',
          inLanguage: 'zh-CN',
        }}
      />
      <main id="main" className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* 品牌介绍 */}
          <section className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-600">
                <span className="inline-flex h-1.5 w-6 rounded-full bg-accent-500" aria-hidden="true" />
                关于我们
              </div>
              <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                让数码爆料，回归可信与有用
              </h1>
              <p className="mt-5 text-[15px] leading-8 text-foreground-700 md:text-base md:leading-8">
                {siteName} 是一家专注于手机与数码新品一手消息的内容平台。我们聚焦<b className="font-semibold text-foreground-900">手机参数曝光、未发布产品图、配置价格预测</b>与<b className="font-semibold text-foreground-900">厂商内部消息</b>，把散落在各处的零散线索，整理成普通读者也能读懂、可用、可信的信息流。
              </p>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700 md:text-base md:leading-8">
                在这个信息过载的时代，我们更愿意做“减法”：不追逐流量噱头，不为博眼球夸大其词，而是以编辑的职业判断，把真正值得关注的新机动向第一时间带到你面前。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-2xl font-black text-primary-600">五类</p>
                  <p className="mt-1 text-xs text-foreground-600">栏目细分内容</p>
                </div>
                <div className="rounded-2xl border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-2xl font-black text-accent-600">多源</p>
                  <p className="mt-1 text-xs text-foreground-600">信源交叉验证</p>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-3xl border border-background-200 shadow-soft">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20illustration%20of%20a%20modern%20digital%20newsroom%20with%20screens%20showing%20smartphone%20spec%20charts%20and%20blueprints%2C%20ink%20and%20flame%20orange%20color%20palette%2C%20minimal%20creative%20magazine%20artwork&width=1200&height=900&seq=about-office-001&orientation=portrait"
                alt="每日新料编辑部创作概念图"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </section>

          {/* 网站定位 & 内容原则 */}
          <section className="mt-16 md:mt-20">
            <div className="max-w-2xl">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-600">
                <span className="inline-flex h-1.5 w-6 rounded-full bg-accent-500" aria-hidden="true" />
                网站定位
              </div>
              <h2 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">我们做什么，又坚持什么</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                三类核心内容，配合一套长期不变的内容原则，构成 {siteName} 的日常。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {principles.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200 bg-background-100 p-6 transition-colors hover:border-accent-200">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-xl text-background-50">
                    <i className={p.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 发展理念 */}
          <section className="mt-16 rounded-3xl border border-background-200 bg-background-100 p-8 md:p-12 md:mt-20">
            <div className="max-w-2xl">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-600">
                <span className="inline-flex h-1.5 w-6 rounded-full bg-accent-500" aria-hidden="true" />
                发展理念
              </div>
              <h2 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">从信息差，到可信赖的内容伙伴</h2>
            </div>
            <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {steps.map((s, i) => (
                <li key={s.title} className="relative">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-black text-background-50">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-widest text-foreground-500">{s.tag}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{s.text}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* 读者口碑 */}
          <section className="mt-16 md:mt-20">
            <h2 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">一路同行的读者</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              {aboutReviews.map((r) => (
                <figure key={r.name} className="rounded-2xl border border-background-200 bg-background-50 p-6">
                  <div className="flex items-center gap-0.5 text-accent-600" aria-label="五星好评">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-7 text-foreground-700">“{r.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">{r.name.charAt(0)}</span>
                    <span className="text-sm font-semibold text-foreground-900">{r.name}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          {/* 联系方式 */}
          <section id="contact" className="mt-16 md:mt-20">
            <div className="grid grid-cols-1 gap-10 rounded-3xl border border-background-200 bg-background-100 p-8 md:p-12 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">联系我们</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                  无论是爆料线索投稿、内容更正，还是合作意向，都欢迎留言。我们会认真对待每一条反馈。
                </p>
                <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-accent-600"><i className="ri-mail-line" aria-hidden="true" /></span>
                    <a href="mailto:hello@meiri-xinliao.example" className="hover:text-primary-700" rel="nofollow">hello@meiri-xinliao.example</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-accent-600"><i className="ri-chat-3-line" aria-hidden="true" /></span>
                    爆料线索 / 投稿通道：欢迎在留言区注明来源阶段
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-accent-600"><i className="ri-rss-line" aria-hidden="true" /></span>
                    最新内容：<Link to="/news" className="hover:text-primary-700">进入资讯中心</Link>
                  </li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <Link key={c.slug} to={`/news?cat=${c.slug}`} className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-600 hover:text-primary-700">
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3">
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}